import { MEDALLION_INK, SectionTitle } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import { FEATURE_FAMILIES } from './feature-families';
import { FeatureRow } from './FeatureRow';
import { FEATURES } from './features';

import type { FeatureFamily } from './feature-families';
import type { ReactElement } from 'react';

/**
 * The capability ledger — the fourteen product capabilities, grouped into four
 * families and read as rows rather than as a wall of peer cards.
 *
 * ## What this replaced, and why
 *
 * One four-column grid holding all fourteen. It was mechanically clean and
 * failed the page anyway: fourteen identical white cards give a reader no entry
 * point, the titles were engineering nouns a non-technical buyer cannot turn
 * into an outcome, and fourteen does not divide by four — so every desktop
 * width ended on a two-card row with a ~700px hole beside it.
 *
 * The families are the fix for the first two and the ROW is the fix for the
 * third. A list has no column arithmetic, so no arrangement of it can leave a
 * hole; the old defect is not corrected here, it is unrepresentable.
 *
 * ## The two-column band
 *
 * Each family is a sticky-headed two-column band: the family's index, name and
 * one-sentence promise on the left, its capabilities on the right. The left
 * column is `lg:sticky` so the promise stays beside the rows it is promising
 * about while a reader scrolls the family — which is the whole argument for
 * spending a third of the width on a heading.
 *
 * Grounds alternate (`page` / `sunk`) so the four bands separate without a
 * rule between them, and the index numeral is the one place the page paints
 * the brand ramp large.
 */
function FamilyBand({ family, index }: { family: FeatureFamily; index: number }): ReactElement {
  const { t } = useT();
  const members = family.slugs
    .map((slug) => FEATURES.find((feature) => feature.slug === slug))
    .filter((feature): feature is (typeof FEATURES)[number] => feature !== undefined);
  const headingId = `feature-family-${family.id}`;
  return (
    <section
      aria-labelledby={headingId}
      className={[
        'border-t border-[color:var(--bd-line-3)] px-6 py-16 sm:py-20',
        // Alternating grounds rather than a divider: four bands separated by
        // four identical rules read as a table, which is the register this page
        // was trying to get out of.
        index % 2 === 0 ? 'bg-[color:var(--bd-page)]' : 'bg-[color:var(--bd-sunk)]',
      ].join(' ')}
    >
      <div className="mx-auto grid max-w-6xl gap-x-14 gap-y-9 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]">
        <div className="lg:sticky lg:top-[calc(var(--bd-nav-h)+2.5rem)] lg:self-start">
          {/* The index takes the FAMILY's hue rather than the shared gradient.
              Four numerals painted from one ramp all read as the same colour at
              this size — the ramp only shows across a long word — so the device
              that was supposed to key each band to its icons keyed nothing.
              The ink is the medallion's own, so the numeral and the row icons
              beside it are provably the same colour and are measured by one
              test. */}
          <span
            aria-hidden="true"
            className="block font-mono text-[2.6rem] font-semibold leading-none tracking-[-0.05em]"
            style={{ color: MEDALLION_INK[family.hue] }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <SectionTitle as="h2" id={headingId} className="mt-4 text-[clamp(1.4rem,2.2vw,1.75rem)]">
            {t(`features.family.${family.id}.name`)}
          </SectionTitle>
          <p className="mt-3 text-pretty text-[0.95rem] leading-[1.66] text-[color:var(--bd-body)]">
            {t(`features.family.${family.id}.promise`)}
          </p>
        </div>
        <ul aria-labelledby={headingId} className="min-w-0">
          {members.map((feature) => (
            <FeatureRow key={feature.slug} feature={feature} hue={family.hue} />
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * The four bands, and nothing above them.
 *
 * The intro deliberately does NOT live here. `/features` already opens on a
 * `PageHero`, and this component used to render a second centred heading
 * underneath it — so the page said "Features / everything your agent can do"
 * and then, 130px of white later, "What ships when you embed Syncanix /
 * grouped by the job each one does". Two mastheads for one page, each weakening
 * the other. The page now passes the ledger's own heading to the hero, so there
 * is one.
 */
export function FeaturesGrid(): ReactElement {
  return (
    <>
      {FEATURE_FAMILIES.map((family, index) => (
        <FamilyBand key={family.id} family={family} index={index} />
      ))}
    </>
  );
}
